import {useLicenceCountry} from '@/modules/licenses/api'
import {
  useCheckDuplicatePatientIdentifiers,
  useDuplicatePatientIdentifiers,
} from '@/modules/patients/api/use-duplicate-patient-identifiers'
import {DuplicatePatientNotice} from '@/modules/patients/components/duplicate-patient-notice'
import {useManualEntryBlocked} from '@/shared/hooks/use-manual-entry-flags'
import {useZero} from '@/shared/integrations/zero/use-zero'
import {Box, Button, ButtonGroup, DateValue, Grid, parseDate, useDialogContext, VStack} from '@chakra-ui/react'
import {Gender, Sex, Title} from '@cypro/engine/database/schema'
import {Patient} from '@cypro/engine/schema'
import {normalizePatientIdentifier} from '@cypro/engine/utils'
import {DialogBody, DialogFooter, DialogHeader} from '@cypro/ui/components'
import {AddressFields} from '@cypro/ui/components/form/address/address-fields'
import {AddressFieldsSchema, getAddressFieldsDefaultValues} from '@cypro/ui/components/form/address/utils'
import {focusFirstErrorField, useAppForm} from '@cypro/ui/integrations/form'
import {validatePhone} from '@cypro/ui/schemas'
import {toaster} from '@cypro/ui/toaster'
import {emailRegex} from '@cypro/ui/utils/regex'
import {revalidateLogic, useStore} from '@tanstack/react-form'
import {useTranslate} from '@tolgee/react'
import * as z from 'zod'
import {genderList, sexList, titleList} from '../../constants'
import {patientContact} from '../../functions'
import {isDefined} from '@cypro/ui/utils'

const PatientDetailsSchema = z.object({
  sex: Sex.nonoptional(),
  gender: Gender.nonoptional(),
  title: Title,
  givenName: z.string().min(1),
  surname: z.string().min(1),
  birthDate: z.any().nonoptional(),
  email: z
    .string()
    .trim()
    .refine(value => value === '' || emailRegex.test(value), {message: 'invalid_email'}),
  phone: validatePhone({required: false}),
  businessPhone: validatePhone({required: false}),
  nationality: z.string(),
  nationalId: z.string(),
  address: AddressFieldsSchema,
  hospitalPatientId: z.string(),
  barcodePrintFlag: z.boolean(),
  notes: z.string(),
})
type PatientDetailsSchema = z.infer<typeof PatientDetailsSchema>

interface Props {
  patient: Patient
}

function changedIdentifier(next: string, prev: string | null | undefined) {
  return normalizePatientIdentifier(next) !== normalizePatientIdentifier(prev) ? next : undefined
}

export function PatientInformationDialog({patient}: Props) {
  const {t} = useTranslate('common')
  const {manualPatientsBlocked} = useManualEntryBlocked()
  const licenceCountry = useLicenceCountry()
  const zero = useZero()
  const context = useDialogContext()
  const checkDuplicateIdentifiers = useCheckDuplicatePatientIdentifiers()

  const titleItems = titleList.map(item => ({value: item, label: t(item)}))
  const sexItems = sexList.map(item => ({value: item, label: t(item)}))
  const genderItems = genderList.map(item => ({value: item, label: t(item)}))
  const todayDate = parseDate(new Date().toISOString().split('T')[0])

  const homeContact = patientContact(patient, 'home')
  const birthDateDefault: DateValue[] = (() => {
    if (!isDefined(patient?.birthDate)) return []
    const d = new Date(patient.birthDate)
    const yyyy = d.getUTCFullYear()
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
    const dd = String(d.getUTCDate()).padStart(2, '0')
    return [parseDate(`${yyyy}-${mm}-${dd}`)]
  })()

  const form = useAppForm({
    defaultValues: {
      sex: patient?.sex ?? null,
      gender: patient?.gender ?? null,
      title: patient?.title ?? null,
      givenName: patient?.givenName || '',
      surname: patient?.familyName || '',
      birthDate: birthDateDefault,
      email: homeContact?.email || '',
      phone: homeContact?.phone || '',
      businessPhone: homeContact?.businessPhone || '',
      nationality: patient?.nationality || '',
      nationalId: patient?.nationalId || '',
      address: getAddressFieldsDefaultValues(homeContact?.address, {country: licenceCountry}),
      hospitalPatientId: patient?.hospitalPatientId || '',
      barcodePrintFlag: Boolean(patient?.barcodePrintFlag),
      notes: patient?.notes || '',
    } as PatientDetailsSchema,
    validators: {
      onDynamic: PatientDetailsSchema,
    },
    validationLogic: revalidateLogic(),
    async onSubmit(props) {
      const duplicateField = await checkDuplicateIdentifiers(changedIdentifiers(props.value), patient.id)
      if (duplicateField) return

      const payload = {
        id: patient.id,
        sex: props.value.sex,
        gender: props.value.gender,
        title: props.value.title,
        givenName: props.value.givenName,
        surname: props.value.surname,
        birthDate: props.value.birthDate[0].toDate('UTC').getTime(),

        email: props.value.email,
        phone: props.value.phone,
        businessPhone: props.value.businessPhone,
        address: props.value.address,
        nationality: props.value.nationality,
        nationalId: props.value.nationalId,
        hospitalPatientId: props.value.hospitalPatientId,
        barcodePrintFlag: props.value.barcodePrintFlag,
        notes: props.value.notes,
      }
      return zero.attemptMutation(zero.mutators.patients.updateDetails(payload), async mutation => {
        const data = await mutation.server
        if (!data) return
        toaster.success({title: t('success'), description: t('patient_updated_successfully')})
        onClose()
      })
    },
    onSubmitInvalid() {
      focusFirstErrorField()
    },
  })

  // Only changed numbers are checked, so pre-existing duplicates don't block unrelated edits.
  function changedIdentifiers(values: Pick<PatientDetailsSchema, 'nationalId' | 'hospitalPatientId'>) {
    return {
      nationalId: changedIdentifier(values.nationalId, patient.nationalId),
      hospitalPatientId: changedIdentifier(values.hospitalPatientId, patient.hospitalPatientId),
    }
  }

  const duplicates = useDuplicatePatientIdentifiers(
    changedIdentifiers({
      nationalId: useStore(form.store, state => state.values.nationalId),
      hospitalPatientId: useStore(form.store, state => state.values.hospitalPatientId),
    }),
    {excludePatientId: patient.id}
  )

  function onClose() {
    context.setOpen(false)
  }

  function onSexChange(sex: string) {
    const isGenderPristine = Boolean(form.fieldInfo.gender?.instance?.state.meta.isPristine)
    if (!isGenderPristine) return

    const relevantGender = genderList.find(g => g === sex)
    if (!relevantGender) return

    form.setFieldValue('gender', relevantGender, {dontUpdateMeta: true})
  }

  const isDefaultValue = useStore(form.store, state => state.isDefaultValue)

  return (
    <form
      onSubmit={e => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <DialogHeader>{t('patient_information')}</DialogHeader>

        <DialogBody minHeight="none" overflowY="scroll">
          <VStack gap="5" width="100%">
            <Grid templateColumns="1fr 1fr" gap="4" width="100%">
              <form.AppField
                name="sex"
                mode="value"
                listeners={{
                  onChange({value}) {
                    if (value) onSexChange(value)
                  },
                }}
              >
                {field => (
                  <field.Select
                    label={t('sex')}
                    placeholder={t('eg', {value: sexItems?.[0]?.label})}
                    items={sexItems}
                    contentProps={{zIndex: 2000}}
                    required
                  />
                )}
              </form.AppField>
              <form.AppField name="gender" mode="value">
                {field => (
                  <field.Select
                    label={t('gender')}
                    placeholder={t('eg', {value: genderItems?.[0]?.label})}
                    items={genderItems}
                    contentProps={{zIndex: 2000}}
                    required
                  />
                )}
              </form.AppField>
            </Grid>

            <Grid templateColumns="1fr 1fr" gap="4" width="100%">
              <form.AppField name="givenName">
                {field => <field.TextField label={t('name')} placeholder={t('given_name_placeholder')} required />}
              </form.AppField>
              <form.AppField name="surname">
                {field => <field.TextField label={t('surname')} placeholder={t('surname_placeholder')} required />}
              </form.AppField>
            </Grid>

            <Grid templateColumns="1fr 1fr" gap="4" width="100%">
              <form.AppField name="birthDate">
                {field => (
                  <field.DatePicker label={t('birth_date')} maxDate={todayDate} required />
                )}
              </form.AppField>
              <form.AppField name="title" mode="value">
                {field => (
                  <field.Select
                    label={t('title')}
                    placeholder={t('select')}
                    items={titleItems}
                    contentProps={{zIndex: 2000}}
                  />
                )}
              </form.AppField>
            </Grid>

            <form.AppField name="email">
              {field => <field.TextField type="email" label={t('email')} placeholder={t('email_placeholder')} />}
            </form.AppField>

            <form.AppField name="phone">
              {field => <field.PhoneField label={t('phone')} placeholder={t('phone_placeholder')} />}
            </form.AppField>

            <form.AppField name="businessPhone">
              {field => <field.PhoneField label={t('business_phone')} placeholder={t('phone_placeholder')} />}
            </form.AppField>

            <form.AppField name="nationality">
              {field => <field.TextField label={t('nationality')} placeholder={t('nationality_placeholder')} />}
            </form.AppField>

            <form.AppField name="hospitalPatientId">
              {field => (
                <Box width="100%">
                  <field.TextField
                    label={t('hospital_patient_id')}
                    placeholder={t('hospital_patient_id_placeholder')}
                    aria-invalid={Boolean(duplicates.hospitalPatientId)}
                  />
                  <DuplicatePatientNotice patient={duplicates.hospitalPatientId} />
                </Box>
              )}
            </form.AppField>

            <form.AppField name="nationalId">
              {field => (
                <Box width="100%">
                  <field.TextField
                    label={t('national_id_number')}
                    placeholder={t('national_id_placeholder')}
                    aria-invalid={Boolean(duplicates.nationalId)}
                  />
                  <DuplicatePatientNotice patient={duplicates.nationalId} />
                </Box>
              )}
            </form.AppField>

            <AddressFields form={form} fields={'address'} title={t('address')} />

            <form.AppField name="barcodePrintFlag">
              {field => <field.Switch label={t('allow_barcode_id_print')} />}
            </form.AppField>
            <Box width="100%" mt="6">
              <form.AppField name="notes">
                {field => <field.Textarea label={t('notes')} placeholder={t('notes_on_the_patient')} />}
              </form.AppField>
            </Box>
          </VStack>
        </DialogBody>

        <DialogFooter>
          <ButtonGroup size="md" colorPalette="cyan" width="100%" justifyContent="flex-end">
            <Button variant="outline" colorPalette="gray" onClick={onClose}>
              {t('cancel')}
            </Button>
            <form.SubmitButton disabled={isDefaultValue || manualPatientsBlocked}>{t('save')}</form.SubmitButton>
          </ButtonGroup>
        </DialogFooter>
      </form.AppForm>
    </form>
  )
}
